import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { IonModal } from '@ionic/angular';
import { DashboardService } from 'app/services/dashboard.service';
import { UserService } from 'app/services/user.service';
import { TimePeriodDropDownType } from 'app/utility/enums';

@Component({
    selector: 'app-time-period-picker',
    templateUrl: './time-period-picker.component.html',
    styleUrls: ['./time-period-picker.component.scss'],
    standalone: false
})
export class TimePeriodPickerComponent implements OnInit {

  @ViewChild('startDatePickerModal') startDatePickerModal: IonModal;
  @ViewChild('endDatePickerModal') endDatePickerModal: IonModal;

  @Output() selectedDatesChanged: EventEmitter<{ startDate: string, endDate: string, timePeriod: TimePeriodDropDownType}> = 
    new EventEmitter<{ startDate: string, endDate: string, timePeriod: TimePeriodDropDownType }>();

  timePeriodOptionText: string = 'This month';
  timePeriodDateText: string = '';

  timePeriodOptions = Object.values(TimePeriodDropDownType);
  tempTimePeriodOption: TimePeriodDropDownType;
  TimePeriodDropDownType = TimePeriodDropDownType;
  @Input() dateFromIso: string;
  @Input() dateToIso: string;
  @Input() asAtMode = false;
  @Input() asAtDateIso = '';
  @Output() asAtDateChanged = new EventEmitter<string>();
  tempAsAtDateIso = '';

  @Input() selectedTimePeriodOption: TimePeriodDropDownType = TimePeriodDropDownType.ThisMonth;
  dateFrom: Date;
  dateTo: Date;

  invalidDate: boolean = false;
  private previousValidDateFrom: Date;
  private previousValidDateTo: Date;

  constructor(
    private dashboardService: DashboardService,
    private userService: UserService
) { }

  ngOnInit() {
    if (this.asAtMode)
    {
      this.asAtDateIso = this.asAtDateIso || this.formatDateNoTime(new Date());
      this.tempAsAtDateIso = this.asAtDateIso;
      return;
    }

    this.tempTimePeriodOption = this.selectedTimePeriodOption;
    this.applySelectedDates();
    this.previousValidDateFrom = new Date(this.dateFrom);
    this.previousValidDateTo = new Date(this.dateTo);
    // Emit initial dates in correct format
    this.selectedDatesChanged.emit({
      startDate: this.formatDateNoTime(this.dateFrom),
      endDate: this.formatDateNoTime(this.dateTo),
      timePeriod: this.selectedTimePeriodOption
    });
  }
  
  dateAdapter = {
    DATE_FORMAT: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
    parse: (value = "", createDate) => {
      const matches = value.match(this.dateAdapter.DATE_FORMAT);
      if (matches) {
        const day = parseInt(matches[1], 10);
        const month = parseInt(matches[2], 10);
        const year = parseInt(matches[3], 10);

        const date = new Date(year, month - 1, day);
        if (date.getFullYear() !== year || (date.getMonth() + 1) !== month || date.getDate() !== day) {
            this.invalidDate = true;
            return null;
        }
        return createDate(matches[3], matches[2], matches[1]);        
      }
    },
    format(date) {
      return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
    },
  }

  updateSelectedDatePeriodOptionText(event) {
    this.invalidDate = false;
    console.debug(event);
  }

  dateTimePeriodDismissed(event) {
    console.debug(event);
    if (this.asAtMode)
    {
      if (this.invalidDate)
      {
        this.userService.presentAlert('Invalid Date', 'The selected date was invalid.');
        this.tempAsAtDateIso = this.asAtDateIso;
        this.invalidDate = false;
        return;
      }

      if (event.detail.data === 'apply')
      {
        const selectedDate = this.parseIsoDate(this.tempAsAtDateIso);
        if (!selectedDate)
        {
          this.userService.presentAlert('Invalid Date', 'The selected date was invalid.');
          this.tempAsAtDateIso = this.asAtDateIso;
          return;
        }
        this.asAtDateIso = this.formatDateNoTime(selectedDate);
        this.asAtDateChanged.emit(this.asAtDateIso);
      }
      return;
    }

    if (this.invalidDate) {
      this.userService.presentAlert('Invalid Date', 'One or more of the selected dates was invalid.')
      this.dateFrom = new Date(this.previousValidDateFrom);
      this.dateTo = new Date(this.previousValidDateTo);
      this.dateFromIso = this.formatDateNoTime(this.dateFrom);
      this.dateToIso = this.formatDateNoTime(this.dateTo);
      this.setSelectedTimePeriodOptionText();
      this.invalidDate = false;
      this.selectedDatesChanged.emit({ startDate: this.formatDateNoTime(this.dateFrom), endDate: this.formatDateNoTime(this.dateTo), timePeriod: this.selectedTimePeriodOption });
      return;
    }
    if (event.detail.data == 'apply') {
      this.applySelectedDates();
      this.selectedDatesChanged.emit({ startDate: this.formatDateNoTime(this.dateFrom), endDate: this.formatDateNoTime(this.dateTo), timePeriod: this.selectedTimePeriodOption });
    }
    else {
      // cancel
      this.selectedTimePeriodOption = this.tempTimePeriodOption;
    }
  }

  applySelectedDates() {
    const today = new Date();
    const currentDay = today.getDay();

    switch (this.selectedTimePeriodOption) {
      case TimePeriodDropDownType.Today:
        this.dateFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        this.dateTo = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        break;
      case TimePeriodDropDownType.ThisMonth:
        // set dateFrom and dateTo to this month
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        this.dateFrom = firstDayOfMonth;
        this.dateTo = lastDayOfMonth;

        break;
      case TimePeriodDropDownType.LastMonth:
        // set dateFrom and dateTo to last month
        const currentDate = new Date();
        const firstDayOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const lastDayOfLastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        this.dateFrom = firstDayOfLastMonth;
        this.dateTo = lastDayOfLastMonth;

        break;

      case TimePeriodDropDownType.ThisWeek:
        // set dateFrom and dateTo to this week, starting from Monday        
        const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - currentDay + 1);
        const sunday = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (7 - currentDay));
        this.dateFrom = monday;
        this.dateTo = sunday;
        break;
      case TimePeriodDropDownType.LastWeek:
        // set dateFrom and dateTo to last week, starting from Monday
        const mondayLastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - currentDay - 6);
        const sundayLastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - currentDay);
        this.dateFrom = mondayLastWeek;
        this.dateTo = sundayLastWeek;
        break;
      case TimePeriodDropDownType.Last7Days:
        // set dateFrom and dateTo to last week
        const lastWeekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7);
        const lastWeekEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        this.dateFrom = lastWeekStart;
        this.dateTo = lastWeekEnd;

        break;
      case TimePeriodDropDownType.Custom:
        // let dateFromFormattedParts = this.dateFromFormatted.split("/");
        // this.dateFrom= new Date(+dateFromFormattedParts[2], +dateFromFormattedParts[1] - 1, +dateFromFormattedParts[0]);
        this.dateFrom = new Date(this.dateFromIso);
        this.dateFrom = new Date(this.dateFrom.getFullYear(), this.dateFrom.getMonth(), this.dateFrom.getDate());

        //let dateToFormattedParts = this.dateToFormatted.split("/");
        //this.dateTo = new Date(+dateToFormattedParts[2], +dateToFormattedParts[1] - 1, +dateToFormattedParts[0]);

        this.dateTo = new Date(this.dateToIso);
        this.dateTo = new Date(this.dateTo.getFullYear(), this.dateTo.getMonth(), this.dateTo.getDate());

        break;
    }

    this.setSelectedTimePeriodOptionText();

    this.dashboardService.dateFrom = this.dateFrom;
    this.dashboardService.dateTo = this.dateTo;

    this.dateFromIso = this.formatDateNoTime(this.dateFrom);
    this.dateToIso = this.formatDateNoTime(this.dateTo);
  }

  setSelectedTimePeriodOptionText() {
    this.timePeriodDateText = this.formatDate(this.dateFrom) + ' - ' + this.formatDate(this.dateTo);

    switch (this.selectedTimePeriodOption) {
      case TimePeriodDropDownType.Today:
        this.timePeriodOptionText = 'Today';
        break;
      case TimePeriodDropDownType.ThisMonth:
        this.timePeriodOptionText = 'This month';
        break;
      case TimePeriodDropDownType.LastMonth:
        this.timePeriodOptionText = 'Last month';
        break;
      case TimePeriodDropDownType.Custom:
        this.timePeriodOptionText = 'Custom';
        this.timePeriodDateText = this.formatDate(this.dateFrom) + ' - ' + this.formatDate(this.dateTo);
        break;
      default:
        this.timePeriodOptionText = this.selectedTimePeriodOption.toString();
    }
  }

  formatDate(date: Date) {
    //format the date in dd/MM/yyyy format
    return date.toLocaleDateString('en-GB');
  }

  closeStartDatePickerModal() {
    //this.startDatePickerModal.dismiss();
  }
  closeEndDatePickerModal() {
    //this.endDatePickerModal.dismiss();
  }

  onDateFromChange(event: CustomEvent) {
    this.dateFromIso = event.detail.value;
  }

  onDateToChange(event: CustomEvent) {
    this.dateToIso = event.detail.value;
  }

  get asAtDateText(): string
  {
    const date = this.parseIsoDate(this.asAtDateIso);
    return date ? this.formatDate(date) : '';
  }

  prepareAsAtDate(): void
  {
    if (!this.asAtMode)
    {
      return;
    }
    this.tempAsAtDateIso = this.asAtDateIso;
    this.invalidDate = false;
  }

  onAsAtDateChange(event: CustomEvent): void
  {
    const date = this.parseIsoDate(event?.detail?.value);
    if (!date)
    {
      this.invalidDate = true;
      return;
    }
    this.tempAsAtDateIso = this.formatDateNoTime(date);
    this.invalidDate = false;
  }

  formatDateNoTime(date: Date): string {
    let year = date.getFullYear();

    // JavaScript counts months from 0 to 11. January is 0. December is 11.
    // So, add 1 to get the correct month number.
    let month = (date.getMonth() + 1).toString().padStart(2, '0');
    let day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private parseIsoDate(value: string): Date | null
  {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match)
    {
      return null;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day)
    {
      return null;
    }
    return date;
  }
}
